import React, { forwardRef, useImperativeHandle, useState, useEffect, useRef } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
    Divider, FormControl, FormHelperText, OutlinedInput, Select, MenuItem,
    RadioGroup, FormControlLabel, Radio, Button, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Paper, Dialog, DialogTitle, DialogContent,
    DialogActions, CircularProgress, IconButton, InputAdornment, Typography, Tooltip
} from '@material-ui/core';
import Grid from "@material-ui/core/Grid";
import LabelValue from "../../../shared/labelvalue/LabelValue.js";
import useFormularioInscricao from './useFormularioInscricao';
import * as Yup from "yup";
import _ from "lodash";
import Add from '@material-ui/icons/AddOutlined';
import Delete from '@material-ui/icons/DeleteOutlined';
import Edit from '@material-ui/icons/EditOutlined';
import Search from '@material-ui/icons/SearchOutlined';
import Close from '@material-ui/icons/CloseOutlined';
import useFetch from "../../../../hooks/useFetch.js";
import { URL_GRAU_PARENTESCO, URL_TIPO_MORADIA, URL_TIPO_HABITACAO, URL_ENDERECO_TIPOS } from "../../../../constants";
import DataGrid from '../../../shared/datagrid/DataGrid.tsx';
import ToolBar from '../../../shared/toolbar/ToolBar';
import DialogRenda from './DialogRenda.jsx';
import DialogMembroFamiliar from './DialogMembroFamiliar.jsx';
import DialogParenteBeneficiario from './DialogParenteBeneficiario.jsx';
import DialogEnderecoComposicaoFamiliar from './DialogEnderecoComposicaoFamiliar.jsx';
import useDialogState from '../../../../hooks/useDialogState.js';
import { containedButton, tituloSecao } from "../../../../utils/styles";
import Message from '../../../shared/message/Message.js';
import InstructionMessage from '../../../shared/instructionmessage/InstructionMessage.js';
import GenericTab from '../../../shared/generictab/GenericTab.js';
import { useContextoResumoAcoes } from './ContextoResumoInscricao.jsx';
import { buildTabContent, getStepSections, listarErrosSecao, STEP_IDS, temErroNosCampos } from './inscricaoSections';
import BlocoErro from '../../../shared/listaerros/BlocoErro.js';
import { criarColunaAcoes } from './AcoesLinha.jsx';
import useConfirmationDialog from "../../../shared/confirmationdialog/useConfirmationDialog";
import useErrorMessage from "../../../../hooks/useErrorMessage";
import { v4 as uuidv4 } from 'uuid';
import { vinculosDoMembro } from './documentosRegras';
import { consolidarOcupacoes, descricaoTipoRenda, formatarMoeda, somarRendas } from './rendaMembro';
import { ValorMonetarioNumberFormatCustom } from "../../../shared/formatters";
import clsx from "clsx";
import Separator from "../../../shared/separator/Separator.js";
import { criarConfiguracaoFormulario, GRUPOS_FORMULARIO } from './formularioConfiguracao';

//className={clsx(classes.containedButton, classes.fullWidth, classes.criarClienteButton)}

const useStyles = makeStyles(theme => ({
    root: {
        marginBottom: theme.spacing(2)
    },
    tabs: {
        width: '100%',
        minWidth: '180px',
        maxWidth: '220px',
        marginRight: theme.spacing(2),
        borderRight: `1px solid ${theme.palette.divider}`,
        '& .MuiTab-root': {
            '&.MuiButtonBase-root.MuiTab-root.MuiTab-textColorPrimary': {
                width: '100%',
                minWidth: '180px',
                maxWidth: '220px',
                alignItems: 'flex-start',
                textAlign: 'left'
            }
        }
    },
    sectionTitle: {
        ...tituloSecao(theme),
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2)
    },
    tableContainer: {
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(2)
    },
    addButton: {
        marginBottom: theme.spacing(2)
    },
    dialogContent: {
        paddingTop: theme.spacing(2)
    },
    tableHeader: {
        backgroundColor: theme.palette.primary.main
    },
    tableHeaderCell: {
        color: 'white !important',
        fontWeight: 'bold'
    },
    columnFlex: {
        display: 'flex'
    },
    columnSmall: {
        width: '100px'
    },
    ...containedButton(theme, false, false)
}));

export const criarSchemaSocioEconomicos = (formulario = {}) => {
    const config = criarConfiguracaoFormulario(formulario.configuracoesFormulario);
    const rendaObrigatoria = config.isRequired(GRUPOS_FORMULARIO.RENDA);
    const grupoFamiliarObrigatorio = config.isRequired(GRUPOS_FORMULARIO.GRUPO_FAMILIAR);
    const parenteObrigatorio = config.isRequired(GRUPOS_FORMULARIO.PARENTE_BENEFICIARIO);
    const outrosObrigatorio = config.isRequired(GRUPOS_FORMULARIO.OUTROS_TIPOS_DE_RENDA);
    const moradiaObrigatoria = config.isRequired(GRUPOS_FORMULARIO.MORADIA);
    const disponibilidadeObrigatoria = config.isRequired(GRUPOS_FORMULARIO.VALOR_DISPONIVEL);

    return Yup.object().shape({
    declaracaoIR: Yup.string().nullable(),
    cadUnicoOuTransferenciaRenda: Yup.boolean().nullable().test(
        'cad-unico-obrigatorio', 'Campo obrigatório',
        value => formulario.programaId !== 5 || value !== null && value !== undefined
    ),
    tipoMoradia: Yup.object().nullable().test(
        'tipo-moradia-obrigatorio', 'Tipo de moradia é obrigatório',
        value => !moradiaObrigatoria || value !== null && value !== undefined
    ),
    tipoHabitacao: Yup.object().nullable().test(
        'tipo-habitacao-obrigatorio', 'Tipo de habitação é obrigatório',
        value => !moradiaObrigatoria || value !== null && value !== undefined
    ),
    vlrPensao: Yup.number().typeError('Deve ser um número').nullable(),
    vlrPensaoFilho: Yup.number().typeError('Deve ser um número').nullable(),
    disponibilidade: Yup.number().typeError('Deve ser um número').nullable().test(
        'disponibilidade-obrigatoria',
        'É necessário informar um valor disponível de pagamento do curso',
        value => !disponibilidadeObrigatoria || (value !== null && value !== undefined && value !== 0)
    ),
    rendas: Yup.array().min(rendaObrigatoria ? 1 : 0, 'Por favor, informe ao menos uma fonte de renda').of(
        Yup.object().shape({
            tpRenda: Yup.string().required(),
            vlrRenda: Yup.number().typeError('Deve ser um número').required('Valor é obrigatório'),
            ocupacao: Yup.string().required('Ocupação é obrigatória')
        })
    ),
    composicaoFamiliar: Yup.array().min(grupoFamiliarObrigatorio ? 1 : 0, 'É necessário informar os membros do grupo familiar').of(
        Yup.object().shape({
            descricao: Yup.string().required('Nome é obrigatório'),
            rendaBruta: Yup.number().typeError('Deve ser um número').nullable(),
            grauParentesco: Yup.object().shape({
                descricao: Yup.string().required('Grau de parentesco é obrigatório')
            }).required(),
            ocupacao: Yup.string().nullable(),
            enderecoComposicaoFamiliar: Yup.object().nullable().required('Endereço é obrigatório'),
            cpf: Yup.string().required('CPF é obrigatório'),
            rg: Yup.string().nullable(),
            dataNascimento: Yup.string().required('Data de nascimento é obrigatória'),
            declaracaoIR: Yup.string().nullable()
        })
    ),
    parentesBeneficiarios: Yup.array().min(parenteObrigatorio ? 1 : 0, 'É necessário informar ao menos um parente beneficiário')
    });
};

const FormSocioEconomicos = forwardRef((props, ref) => {
    const classes = useStyles();
    const showConfirmation = useConfirmationDialog();
    const showErrorMessage = useErrorMessage();
    const tabsRef = useRef(null);
    const fetch = useFetch();
    /* somenteConsulta: fora do período de inscrição a etapa abre só para consulta, nada pode ser alterado. */
    const { data, onUpdate, formulario = {}, activeSectionId, somenteConsulta = false } = props;
    const formularioConfig = criarConfiguracaoFormulario(formulario.configuracoesFormulario);
    const mostrarRenda = formularioConfig.isVisible(GRUPOS_FORMULARIO.RENDA);
    const mostrarOutros = formularioConfig.isVisible(GRUPOS_FORMULARIO.OUTROS_TIPOS_DE_RENDA);
    const mostrarMoradia = formularioConfig.isVisible(GRUPOS_FORMULARIO.MORADIA);
    const mostrarDisponibilidade = formularioConfig.isVisible(GRUPOS_FORMULARIO.VALOR_DISPONIVEL);
    const setDadosResumo = useContextoResumoAcoes();

    const {
        dialogState,
        openDialog,
        closeDialog
    } = useDialogState({
        parente: false,
        renda: false,
        membro: false,
        endereco: false
    });

    const [editParenteIndex, setEditParenteIndex] = useState(-1);
    const [editRendaIndex, setEditRendaIndex] = useState(-1);
    const [editMembroIndex, setEditMembroIndex] = useState(-1);
    const [editEnderecoIndex, setEditEnderecoIndex] = useState(-1);

    // Lookups state
    const [tiposMoradia, setTiposMoradia] = useState([]);
    const [tiposHabitacao, setTiposHabitacao] = useState([]);
    const [tiposEndereco, setTiposEndereco] = useState([]);
    const [grausParentesco, setGrausParentesco] = useState([]);

    useEffect(() => {
        const fetchLookups = async () => {
            try {
                const [resMoradia, resHabitacao, resEndereco, resGrausParentesco] = await Promise.all([
                    fetch(URL_TIPO_MORADIA, null, null, null, false),
                    fetch(URL_TIPO_HABITACAO, null, null, null, false),
                    fetch(URL_ENDERECO_TIPOS, null, null, null, true),
                    fetch(URL_GRAU_PARENTESCO, null, null, null, false)
                ]);
                setTiposMoradia(resMoradia || []);
                setTiposHabitacao(resHabitacao || []);
                setTiposEndereco(resEndereco || []);
                setGrausParentesco(resGrausParentesco || []);
            } catch (error) {
                // O useFetch já avisa o usuário da falha; as listas ficam vazias.
            }
        };
        fetchLookups();
    }, []);

    const getTipoEnderecoDescricao = (tipo) => {
        if (!tipo) return '';
        if (typeof tipo === 'object') {
            return tipo.descricao || '';
        }
        const found = tiposEndereco.find(t => t.nome === tipo);
        return found ? found.descricao : tipo;
    };

    const formEtapa = useFormularioInscricao({
        valoresIniciais: { ...data },
        reinicializar: true,
        schema: criarSchemaSocioEconomicos(formulario)
    });

    useImperativeHandle(ref, () => ({
        formulario: formEtapa.api
    }));

    useEffect(() => {
        if (setDadosResumo) {
            setDadosResumo(formEtapa.valores);
        }
    }, [formEtapa.valores, setDadosResumo]);



    useEffect(() => {
        if (formEtapa.submitCount > 0 && !formEtapa.valido) {
            const firstErrorIdx = tabContent.findIndex(item => item.hasError);
            if (firstErrorIdx !== -1) {
                tabsRef.current?.setTabIndex(firstErrorIdx);
            }
        }
    }, [formEtapa.submitCount, formEtapa.valido]);

    // --- Parente Beneficiário CRUD ---
    const handleAddParente = () => {
        setEditParenteIndex(-1);
        openDialog('parente')();
    };

    const handleSaveParente = (form) => {
        const current = [...(formEtapa.valores.parentesBeneficiarios || [])];
        const existing = editParenteIndex > -1 ? current[editParenteIndex] : {};
        const grauParentesco = grausParentesco.find(item => item.id === Number(form.grauParentescoId)) || null;
        const item = {
            ...existing,
            nome: form.nome,
            grauParentesco,
            bolsa: form.bolsa
        };
        if (editParenteIndex > -1) {
            current[editParenteIndex] = item;
        } else {
            current.push(item);
        }
        formEtapa.definirValor('parentesBeneficiarios', current);
        closeDialog('parente')();
    };

    const handleEditParente = (index) => {
        setEditParenteIndex(index);
        openDialog('parente')();
    };

    const handleDeleteParente = (index) => {
        showConfirmation('Deseja realmente remover este parente?', () => {
            const current = [...(formEtapa.valores.parentesBeneficiarios || [])];
            current.splice(index, 1);
            formEtapa.definirValor('parentesBeneficiarios', current);
        });
    };

    // --- Rendas do Candidato CRUD ---
    const handleAddRenda = () => {
        setEditRendaIndex(-1);
        openDialog('renda')();
    };

    const handleSaveRenda = (form) => {
        const current = [...(formEtapa.valores.rendas || [])];
        const item = {
            tpRenda: form.tpRenda,
            vlrRenda: parseFloat(form.vlrRenda),
            ocupacao: form.ocupacao
        };
        if (editRendaIndex > -1) {
            current[editRendaIndex] = item;
        } else {
            current.push(item);
        }
        formEtapa.definirValor('rendas', current);
        closeDialog('renda')();
    };

    const handleEditRenda = (index) => {
        setEditRendaIndex(index);
        openDialog('renda')();
    };

    const handleDeleteRenda = (index) => {
        showConfirmation('Deseja realmente remover esta fonte de renda?', () => {
            const current = [...(formEtapa.valores.rendas || [])];
            current.splice(index, 1);
            formEtapa.definirValor('rendas', current);
        });
    };

    // --- Composição Familiar CRUD ---
    const handleAddMembro = () => {
        setEditMembroIndex(-1);
        openDialog('membro')();
    };

    const handleSaveMembro = (form) => {
        const current = [...(formEtapa.valores.composicaoFamiliar || [])];
        const existing = editMembroIndex > -1 ? current[editMembroIndex] : {};
        const grauParentesco = grausParentesco.find(item => item.id === Number(form.grauParentescoId)) || null;

        // Ocupação e renda bruta do integrante: consolidação das ocupações/rendas
        // da lista, como o back grava em DSOCUPACAO/RENDABRUTA.
        const rendas = (form.rendas || []).map(renda => ({ ...renda, vlrRenda: parseFloat(renda.vlrRenda) || 0 }));
        const item = {
            ...existing,
            descricao: form.descricao,
            rendas,
            rendaBruta: somarRendas(rendas),
            grauParentesco,
            ocupacao: consolidarOcupacoes(rendas),
            enderecoComposicaoFamiliar: form.enderecoComposicaoFamiliar,
            cpf: form.cpf || null,
            rg: form.rg || null,
            dataNascimento: form.dataNascimento || null,
            declaracaoIR: form.declaracaoIR || 'N'
        };
        if (editMembroIndex > -1) {
            current[editMembroIndex] = item;
        } else {
            /*
             * Membro novo (sem id do back): recebe uma chave local própria,
             * que o spread `...existing` acima preserva nas próximas edições.
             * Sem ela, cada reinicialização do useForm clona o objeto e o
             * vínculo com os documentos já anexados a este membro (guardados
             * por referência em anexo.composicaoFamiliar) se perderia assim
             * que um dado dele — o CPF, por exemplo — fosse corrigido.
             */
            item.chaveLocal = uuidv4();
            current.push(item);
        }
        formEtapa.definirValor('composicaoFamiliar', current);
        closeDialog('membro')();
    };

    const handleEditMembro = (index) => {
        setEditMembroIndex(index);
        openDialog('membro')();
    };

    const handleDeleteMembro = (index) => {
        const membro = formEtapa.valores.composicaoFamiliar[index];
        const vinculos = vinculosDoMembro(membro, {
            anexos: formEtapa.valores.inscricaoAnexos || [],
            pendencias: data.inscricaoPendenciaAnexos || []
        });

        // Documento salvo ou pendência: só a etapa Documentos (ou a instituição) resolve.
        if (vinculos.pendencias.length > 0 || vinculos.anexosSalvos.length > 0) {
            showErrorMessage(`${membro.descricao} tem documento enviado ou pendência de documento e não pode ser removido aqui. Remova os documentos na etapa Documentos ou procure a instituição.`);
            return;
        }

        // Só documento novo (ainda não salvo): confirma e remove os dois juntos, pela mesma instância.
        if (vinculos.anexosNovos.length > 0) {
            showConfirmation(
                'Este membro tem documentos adicionados que ainda não foram salvos. Ao removê-lo, esses documentos também serão removidos. Deseja continuar?',
                () => {
                    const membros = [...(formEtapa.valores.composicaoFamiliar || [])];
                    membros.splice(index, 1);

                    const anexos = (formEtapa.valores.inscricaoAnexos || [])
                        .filter(anexo => !vinculos.anexosNovos.includes(anexo));

                    formEtapa.definirValor('composicaoFamiliar', membros);
                    formEtapa.definirValor('inscricaoAnexos', anexos);
                }
            );
            return;
        }

        showConfirmation('Deseja realmente remover este membro da família?', () => {
            const current = [...(formEtapa.valores.composicaoFamiliar || [])];
            current.splice(index, 1);
            formEtapa.definirValor('composicaoFamiliar', current);
        });
    };

    // --- Endereços do Grupo Familiar CRUD ---
    const handleAddEndereco = () => {
        setEditEnderecoIndex(-1);
        openDialog('endereco')();
    };

    const handleSaveEndereco = (form) => {
        const current = [...(formEtapa.valores.enderecoComposicaoFamiliares || [])];
        const existing = editEnderecoIndex > -1 ? current[editEnderecoIndex] : {};
        const item = {
            ...existing,
            tipoEndereco: form.tipoEndereco,
            cep: form.cep,
            logradouro: form.logradouro,
            numero: form.numero,
            complemento: form.complemento,
            bairro: form.bairro,
            cidade: form.cidade,
            uf: form.uf
        };
        if (editEnderecoIndex > -1) {
            current[editEnderecoIndex] = item;
        } else {
            current.push(item);
        }
        formEtapa.definirValor('enderecoComposicaoFamiliares', current);
        closeDialog('endereco')();
    };

    const handleEditEndereco = (index) => {
        setEditEnderecoIndex(index);
        openDialog('endereco')();
    };

    const handleDeleteEndereco = (index) => {
        showConfirmation('Deseja realmente remover este endereço?', () => {
            const current = [...(formEtapa.valores.enderecoComposicaoFamiliares || [])];
            current.splice(index, 1);
            formEtapa.definirValor('enderecoComposicaoFamiliares', current);
        });
    };

    const rendaColumns = [
        { label: 'Tipo', field: 'tpRenda', valueGetter: (val) => descricaoTipoRenda(val) },
        { label: 'Ocupação', field: 'ocupacao' },
        { label: 'Valor Bruto', field: 'vlrRenda', valueGetter: (val) => val.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) },
        ...(somenteConsulta ? [] : [criarColunaAcoes(row => {
            const idx = formEtapa.valores.rendas.indexOf(row);
            return [
                { titulo: 'Editar', icone: <Edit fontSize="small" />, onClick: () => handleEditRenda(idx) },
                { titulo: 'Remover', icone: <Delete fontSize="small" />, onClick: () => handleDeleteRenda(idx) }
            ];
        }, { className: classes.columnSmall })])
    ];

    const membroColumns = [
        { label: 'Nome', field: 'descricao' },
        { label: 'Parentesco', field: 'grauParentesco.descricao' },
        { label: 'Ocupações', field: 'ocupacao', valueGetter: (val) => val || 'Nenhuma informada' },
        { label: 'Tipo', field: 'enderecoComposicaoFamiliar', valueGetter: (val) => val ? getTipoEnderecoDescricao(val.tipoEndereco) : 'Não associado' },
        { label: 'Endereço', field: 'enderecoComposicaoFamiliar', valueGetter: (val) => val ? `${val.logradouro}, ${val.numero}${val.complemento ? ` - ${val.complemento}` : ''}, ${val.bairro} - ${(val.cidade?.nome || val.cidade || '')}/${val.uf || val.cidade?.uf?.uf || ''}` : 'Não associado' },
        { label: 'Renda Bruta', field: 'rendaBruta', valueGetter: (val) => formatarMoeda(val) },
        ...(somenteConsulta ? [] : [criarColunaAcoes(row => {
            const idx = formEtapa.valores.composicaoFamiliar.indexOf(row);
            return [
                { titulo: 'Editar', icone: <Edit fontSize="small" />, onClick: () => handleEditMembro(idx) },
                { titulo: 'Remover', icone: <Delete fontSize="small" />, onClick: () => handleDeleteMembro(idx) }
            ];
        }, { className: classes.columnSmall })])
    ];

    const parenteColumns = [
        { label: 'Nome', field: 'nome' },
        { label: 'Grau de Parentesco', field: 'grauParentesco.descricao' },
        { label: 'Bolsa/Desconto', field: 'bolsa', valueGetter: (val) => val || 'Não informado' },
        ...(somenteConsulta ? [] : [criarColunaAcoes(row => {
            const idx = formEtapa.valores.parentesBeneficiarios.indexOf(row);
            return [
                { titulo: 'Editar', icone: <Edit fontSize="small" />, onClick: () => handleEditParente(idx) },
                { titulo: 'Remover', icone: <Delete fontSize="small" />, onClick: () => handleDeleteParente(idx) }
            ];
        }, { className: classes.columnSmall })])
    ];

    const enderecoColumns = [
        { label: 'Tipo de Endereço', field: 'tipoEndereco', valueGetter: (val) => getTipoEnderecoDescricao(val) },
        { label: 'Logradouro', field: 'logradouro', valueGetter: (val, row) => `${val}, ${row.numero || ''}` },
        { label: 'Bairro', field: 'bairro' },
        { label: 'Cidade/UF', field: 'cidade', valueGetter: (val, row) => val ? `${val.nome || val} - ${row.uf || val.uf?.uf || ''}` : '' },
        { label: 'CEP', field: 'cep' },
        ...(somenteConsulta ? [] : [criarColunaAcoes(row => {
            const enderecos = formEtapa.valores.enderecoComposicaoFamiliares || [];
            const idx = enderecos.indexOf(row);
            const primaryResidencialIndex = enderecos.findIndex(addr => addr.tipoEndereco === 'RESIDENCIAL');
            /* O endereço residencial principal é do candidato: não se edita nem remove por aqui. */
            const isProtected = row.tipoEndereco === 'RESIDENCIAL' && idx === primaryResidencialIndex;

            return [
                { titulo: 'Editar', icone: <Edit fontSize="small" />, onClick: () => handleEditEndereco(idx), visivel: !isProtected },
                { titulo: 'Remover', icone: <Delete fontSize="small" />, onClick: () => handleDeleteEndereco(idx), visivel: !isProtected }
            ];
        }, { className: classes.columnSmall })])
    ];

    const sections = getStepSections(STEP_IDS.SOCIOECONOMICO, { formulario, inscricao: formEtapa.valores });

    /*
     * Estado de erro de um bloco do formulário: destaca só o bloco afetado e,
     * quando o erro não tem campo próprio onde aparecer, lista o que falta.
     */
    const bloco = (secaoId, ...campos) => {
        if (!formEtapa.submitCount) return { erro: false, itens: [] };

        return {
            erro: temErroNosCampos(formEtapa.erros, campos),
            itens: listarErrosSecao(sections.find(secao => secao.id === secaoId), formEtapa.erros, formEtapa.valores, campos)
        };
    };

    const tabEntries = [
        {
            id: 'declaracaoMoradia',
            content: (
                <Grid container spacing={3}>
                    {mostrarRenda && (
                        <>
                            <Grid item xs={12}>
                                <h4 className={classes.sectionTitle}>Declarações</h4>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <LabelValue label='Declara ou declarou imposto de renda no último ano fiscal? *'>
                                    <FormControl component="fieldset" disabled={somenteConsulta}>
                                        <RadioGroup
                                            name="declaracaoIR"
                                            value={formEtapa.valores.declaracaoIR}
                                            onChange={formEtapa.aoMudar}
                                            row>
                                            <FormControlLabel value="S" control={<Radio color="primary" />} label="Sim" />
                                            <FormControlLabel value="N" control={<Radio color="primary" />} label="Não" />
                                            <FormControlLabel value="I" control={<Radio color="primary" />} label="Isento" />
                                        </RadioGroup>
                                    </FormControl>
                                </LabelValue>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <LabelValue label='Participa do CadÚnico ou outros programas de transferência de renda? *'>
                                    <FormControl component="fieldset" disabled={somenteConsulta} error={formEtapa.submitCount > 0 && !!formEtapa.erros.cadUnicoOuTransferenciaRenda}>
                                        <RadioGroup
                                            name="cadUnicoOuTransferenciaRenda"
                                            value={String(formEtapa.valores.cadUnicoOuTransferenciaRenda ?? '')}
                                            onChange={(e) => formEtapa.definirValor('cadUnicoOuTransferenciaRenda', e.target.value === 'true')}
                                            row
                                        >
                                            <FormControlLabel value="true" control={<Radio color="primary" />} label="Sim" />
                                            <FormControlLabel value="false" control={<Radio color="primary" />} label="Não" />
                                        </RadioGroup>
                                        {formEtapa.submitCount > 0 && <FormHelperText>{formEtapa.erros.cadUnicoOuTransferenciaRenda}</FormHelperText>}
                                    </FormControl>
                                </LabelValue>
                            </Grid>
                        </>
                    )}

                    {mostrarRenda && mostrarMoradia && <Separator noCenterText/>}

                    {mostrarMoradia && (
                        <>
                            <Grid item xs={12}>
                                <h4 className={classes.sectionTitle}>Moradia</h4>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <LabelValue label="Tipo de habitação *">
                                    <FormControl variant="outlined" fullWidth disabled={somenteConsulta} error={formEtapa.tocados.tipoHabitacao && !!formEtapa.erros.tipoHabitacao}>
                                        <Select
                                            name="tipoHabitacao"
                                            value={formEtapa.valores.tipoHabitacao?.id || ''}
                                            onChange={(e) => {
                                                const selectedId = e.target.value;
                                                const selectedObj = tiposHabitacao.find(h => h.id === selectedId);
                                                formEtapa.definirValor('tipoHabitacao', selectedObj || null);
                                            }}
                                        >
                                            <MenuItem value="">Selecione...</MenuItem>
                                            {tiposHabitacao.map(th => (
                                                <MenuItem key={th.id} value={th.id}>{th.descricao}</MenuItem>
                                            ))}
                                        </Select>
                                        {formEtapa.tocados.tipoHabitacao && <FormHelperText>{formEtapa.erros.tipoHabitacao}</FormHelperText>}
                                    </FormControl>
                                </LabelValue>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <LabelValue label="Tipo de moradia *">
                                    <FormControl variant="outlined" fullWidth disabled={somenteConsulta} error={formEtapa.tocados.tipoMoradia && !!formEtapa.erros.tipoMoradia}>
                                        <Select
                                            name="tipoMoradia"
                                            value={formEtapa.valores.tipoMoradia?.id || ''}
                                            onChange={(e) => {
                                                const selectedId = e.target.value;
                                                const selectedObj = tiposMoradia.find(m => m.id === selectedId);
                                                formEtapa.definirValor('tipoMoradia', selectedObj || null);
                                            }}
                                        >
                                            <MenuItem value="">Selecione...</MenuItem>
                                            {tiposMoradia.map(tm => (
                                                <MenuItem key={tm.id} value={tm.id}>{tm.descricao}</MenuItem>
                                            ))}
                                        </Select>
                                        {formEtapa.tocados.tipoMoradia && <FormHelperText>{formEtapa.erros.tipoMoradia}</FormHelperText>}
                                    </FormControl>
                                </LabelValue>
                            </Grid>
                        </>
                    )}
                </Grid>
            )
        },
        {
            id: 'rendas',
            content: (
                <Grid container spacing={3}>
                    {mostrarRenda && (
                        <>
                            <Grid item xs={12}>
                                <BlocoErro {...bloco('rendas', 'rendas')}>
                                    <Grid container spacing={3}>
                                    <Grid item xs={12}>
                                        <h4 className={classes.sectionTitle}>Minhas Fontes de Renda</h4>
                                        <InstructionMessage
                                            title="Orientações sobre Minhas Fontes de Renda"
                                            type="info"
                                            variant="standard"
                                            collapsible={true}
                                            defaultExpanded={true}
                                            items={[
                                                "Cadastre todas as suas fontes de renda ativa (trabalho, estágio, etc.);",
                                                "Caso não possua renda própria, não é necessário adicionar nenhum item nesta tabela."
                                            ]}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <DataGrid
                                            responsive="card"
                                            toolbarContent={!somenteConsulta && (
                                                <Button
                                                    variant="contained"
                                                    className={classes.containedButton}
                                                    startIcon={<Add />}
                                                    onClick={handleAddRenda}
                                                >
                                                    Adicionar
                                                </Button>
                                            )}
                                            tableContainerProps={{
                                                component: 'div'
                                            }}
                                            rows={formEtapa.valores.rendas || []}
                                            columns={rendaColumns}
                                            comparatorKeys={['tpRenda', 'ocupacao']}
                                            paginate
                                            paginateOptions={{ rowsPerPage: 5, startPage: 0, resetPageOnRowsChange: true }}
                                            noRecordsText="Nenhuma renda cadastrada."
                                        />
                                    </Grid>
                                    </Grid>
                                </BlocoErro>
                            </Grid>
                        </>
                    )}

                    {mostrarRenda && mostrarOutros && <Separator noCenterText />}

                    {mostrarOutros && (
                        <>
                            <Grid item xs={12}>
                                <h4 className={classes.sectionTitle}>Outros Tipos de Renda</h4>
                            </Grid>
                            <Grid item xs={12} sm={5}>
                                <LabelValue label="Valor que recebe de pensão alimentícia">
                                    <FormControl fullWidth disabled={somenteConsulta} error={formEtapa.submitCount > 0 && !!formEtapa.erros.vlrPensao}>
                                    <OutlinedInput
                                        fullWidth
                                        name="vlrPensao"
                                        value={formEtapa.valores.vlrPensao ?? ''}
                                        onChange={formEtapa.aoMudar}
                                        inputComponent={ValorMonetarioNumberFormatCustom}
                                        startAdornment={<InputAdornment position="start">R$</InputAdornment>}
                                    />
                                        {formEtapa.submitCount > 0 && <FormHelperText>{formEtapa.erros.vlrPensao}</FormHelperText>}
                                    </FormControl>
                                </LabelValue>
                            </Grid>
                            <Grid item xs={12} sm={7}>
                                <LabelValue label="Valor recebido pelo(s) filho(s) de pensão alimentícia">
                                    <FormControl fullWidth disabled={somenteConsulta} error={formEtapa.submitCount > 0 && !!formEtapa.erros.vlrPensaoFilho}>
                                    <OutlinedInput
                                        fullWidth
                                        name="vlrPensaoFilho"
                                        value={formEtapa.valores.vlrPensaoFilho ?? ''}
                                        onChange={formEtapa.aoMudar}
                                        inputComponent={ValorMonetarioNumberFormatCustom}
                                        startAdornment={<InputAdornment position="start">R$</InputAdornment>}
                                    />
                                        {formEtapa.submitCount > 0 && <FormHelperText>{formEtapa.erros.vlrPensaoFilho}</FormHelperText>}
                                    </FormControl>
                                </LabelValue>
                            </Grid>
                        </>
                    )}

                    {mostrarOutros && mostrarDisponibilidade && <Separator noCenterText />}

                    {mostrarDisponibilidade && (
                        <>
                            <Grid item xs={12}>
                                <h4 className={classes.sectionTitle}>Outras Informações</h4>
                            </Grid>
                            <Grid item xs={12}>
                                <LabelValue label="Valor disponível para pagamento do curso">
                                    <FormControl fullWidth disabled={somenteConsulta} error={formEtapa.submitCount > 0 && !!formEtapa.erros.disponibilidade}>
                                    <OutlinedInput
                                        fullWidth
                                        name="disponibilidade"
                                        value={formEtapa.valores.disponibilidade ?? ''}
                                        onChange={formEtapa.aoMudar}
                                        inputComponent={ValorMonetarioNumberFormatCustom}
                                        startAdornment={<InputAdornment position="start">R$</InputAdornment>}
                                    />
                                        {formEtapa.submitCount > 0 && <FormHelperText>{formEtapa.erros.disponibilidade}</FormHelperText>}
                                    </FormControl>
                                </LabelValue>
                            </Grid>
                        </>
                    )}
                </Grid>
            )
        },
        {
            id: 'grupoFamiliar',
            content: (
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <BlocoErro {...bloco('grupoFamiliar', 'enderecoComposicaoFamiliares')}>
                            <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <h4 className={classes.sectionTitle} >Endereços do Grupo Familiar</h4>
                                <InstructionMessage
                                    title="Orientações sobre Endereço do Grupo Familiar"
                                    type="info"
                                    variant="standard"
                                    collapsible={true}
                                    defaultExpanded={true}
                                    items={[
                                        "Cadastre o(s) endereço(s) onde os membros do grupo familiar residem;",
                                        "Pelo menos um endereço residencial deve ser informado;",
                                        "É possível associar o mesmo endereço a múltiplos membros da família."
                                    ]}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <div>
                                    <DataGrid
                                        responsive="card"
                                        toolbarContent={!somenteConsulta && (
                                            <Button
                                                variant="contained"
                                                className={classes.containedButton}
                                                startIcon={<Add />}
                                                onClick={handleAddEndereco}
                                            >
                                                Adicionar
                                            </Button>
                                        )}
                                        tableContainerProps={{
                                            component: 'div'
                                        }}
                                        rows={formEtapa.valores.enderecoComposicaoFamiliares || []}
                                        columns={enderecoColumns}
                                        comparatorKeys={['id', 'tipoEndereco', 'cep', 'logradouro', 'numero']}
                                        paginate
                                        paginateOptions={{ rowsPerPage: 5, startPage: 0, resetPageOnRowsChange: true }}
                                        noRecordsText="Nenhum endereço registrado."
                                    />
                                </div>
                            </Grid>
                            </Grid>
                        </BlocoErro>
                    </Grid>

                   <Separator noCenterText/>

                    <Grid item xs={12}>
                        <BlocoErro {...bloco('grupoFamiliar', 'composicaoFamiliar')}>
                            <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <h4 className={classes.sectionTitle} >Composição do Grupo Familiar</h4>
                                <InstructionMessage
                                    title="Orientações sobre Grupo familiar"
                                    type="info"
                                    variant="standard"
                                    marginBottom
                                    collapsible={true}
                                    defaultExpanded={true}
                                    items={[
                                        "É importante que sejam informados todos os integrantes do grupo familiar;",
                                        "Clique no botão Adicionar para incluir cada integrante;",
                                        "Na inclusão do integrante, adicione cada ocupação/renda dele (tipo, ocupação e valor bruto mensal); quem não possui renda pode ficar sem ocupação informada;",
                                        "Entende-se como grupo familiar a unidade nuclear composta por uma ou mais pessoas, eventualmente ampliada por outras pessoas que contribuam para o rendimento ou tenham suas despesas atendidas por aquela unidade familiar, todas moradoras em um mesmo domicílio."
                                    ]}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <div >
                                    <DataGrid
                                        responsive="card"
                                        toolbarContent={!somenteConsulta && (
                                            <Button
                                                variant="contained"
                                                className={classes.containedButton}
                                                startIcon={<Add />}
                                                onClick={handleAddMembro}
                                            >
                                                Adicionar
                                            </Button>
                                        )}
                                        tableContainerProps={{
                                            component: 'div'
                                        }}
                                        rows={formEtapa.valores.composicaoFamiliar || []}
                                        columns={membroColumns}
                                        comparatorKeys={['descricao']}
                                        paginate
                                        paginateOptions={{ rowsPerPage: 5, startPage: 0, resetPageOnRowsChange: true }}
                                        noRecordsText="Nenhum membro registrado."
                                    />
                                </div>
                            </Grid>
                            </Grid>
                        </BlocoErro>
                    </Grid>
                </Grid>
            )
        },
        {
            id: 'parentes',
            content: (
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <BlocoErro {...bloco('parentes', 'parentesBeneficiarios')}>
                            <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <h4 className={classes.sectionTitle}>Parentes Beneficiários (que possuem bolsa na UNAERP)</h4>
                                <InstructionMessage
                                    title="Orientações sobre Parente Beneficiário"
                                    type="info"
                                    variant="standard"
                                    marginBottom
                                    collapsible={true}
                                    defaultExpanded={true}
                                    items={[
                                        "É importante que sejam informados todos os parentes que possuem bolsa na Unaerp;",
                                        "Clique no botão Adicionar para incluir cada parente que possui bolsa;",
                                        "Se você (o candidato/estudante) não possuir parente que possui bolsa na UNAERP, não será necessário preencher esse item, prossiga com a sua inscrição."
                                    ]}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <div>
                                    <DataGrid
                                        responsive="card"
                                        toolbarContent={!somenteConsulta && (
                                            <Button
                                                variant="contained"
                                                className={classes.containedButton}
                                                startIcon={<Add />}
                                                onClick={handleAddParente}
                                            >
                                                Adicionar
                                            </Button>
                                        )}
                                        tableContainerProps={{
                                            component: 'div'
                                        }}
                                        rows={formEtapa.valores.parentesBeneficiarios || []}
                                        columns={parenteColumns}
                                        comparatorKeys={['nome']}
                                        paginate
                                        paginateOptions={{ rowsPerPage: 5, startPage: 0, resetPageOnRowsChange: true }}
                                        noRecordsText="Nenhum parente registrado."
                                    />
                                </div>
                            </Grid>
                            </Grid>
                        </BlocoErro>
                    </Grid>
                </Grid>
            )
        }
    ];

    const tabContent = buildTabContent(sections, tabEntries, formEtapa);

    return (
        <form className={classes.root} noValidate autoComplete="off">
            <GenericTab
                ref={tabsRef}
                orientation="vertical"
                noAppBar
                mobileVariant="accordion"
                indicatorColor="primary"
                textColor="primary"
                scrollButtons="auto"
                className={classes.tabs}
                activeId={activeSectionId}
                content={tabContent}
            />

            <DialogRenda
                open={dialogState.renda}
                onClose={closeDialog('renda')}
                onSave={handleSaveRenda}
                initialValues={editRendaIndex > -1 ? formEtapa.valores.rendas[editRendaIndex] : null}
            />

            <DialogMembroFamiliar
                open={dialogState.membro}
                onClose={closeDialog('membro')}
                onSave={handleSaveMembro}
                initialValues={editMembroIndex > -1 ? formEtapa.valores.composicaoFamiliar[editMembroIndex] : null}
                enderecos={formEtapa.valores.enderecoComposicaoFamiliares || []}
                tiposEndereco={tiposEndereco}
                grausParentesco={grausParentesco}
            />

            <DialogParenteBeneficiario
                open={dialogState.parente}
                onClose={closeDialog('parente')}
                onSave={handleSaveParente}
                initialValues={editParenteIndex > -1 ? formEtapa.valores.parentesBeneficiarios[editParenteIndex] : null}
                grausParentesco={grausParentesco}
            />

            <DialogEnderecoComposicaoFamiliar
                open={dialogState.endereco}
                onClose={closeDialog('endereco')}
                onSave={handleSaveEndereco}
                initialValues={editEnderecoIndex > -1 ? formEtapa.valores.enderecoComposicaoFamiliares[editEnderecoIndex] : null}
                tipos={tiposEndereco}
                enderecos={formEtapa.valores.enderecoComposicaoFamiliares}
            />
        </form>
    );
});

FormSocioEconomicos.displayName = 'FormSocioEconomicos';
export default FormSocioEconomicos;
